import { computeAcoustics } from '../acoustics/compute'
import type { SceneDoc } from '../model/schema'
import { AudioEngine } from './engine'

export interface RenderedClick {
  left: Float32Array
  right: Float32Array
  sampleRate: number
}

/**
 * Render the click test for one source through the complete acoustic chain (direct, early reflections,
 * late tail, HRTF) in an OfflineAudioContext. All sources are silenced except for the click itself.
 */
export async function renderClick(
  doc: SceneDoc,
  opts: { sourceId?: string; seconds?: number; sampleRate?: number } = {},
): Promise<RenderedClick> {
  const sampleRate = opts.sampleRate ?? 48000
  const seconds = opts.seconds ?? 3
  const ctx = new OfflineAudioContext(2, Math.ceil(seconds * sampleRate), sampleRate)
  const source = doc.sources.find((s) => s.id === opts.sourceId) ?? doc.sources[0]
  if (!source) throw new Error('Scene has no source to click.')
  const acoustics = computeAcoustics({
    enclosure: doc.enclosure,
    objects: doc.objects,
    listener: doc.listener.position,
    sources: [{ id: source.id, position: source.position, size: source.size }],
  })
  const engine = await AudioEngine.create(ctx, { immediate: true, room: acoustics.room })
  engine.syncSources([{ ...source, playing: false, solo: false, mute: false }])
  engine.setListener(doc.listener)
  engine.applyAcoustics(acoustics)
  engine.clickTest(source.id, 0.05)
  const buf = await ctx.startRendering()
  return { left: buf.getChannelData(0).slice(), right: buf.getChannelData(1).slice(), sampleRate }
}

/**
 * Render a scene with every source playing (generators + full acoustic chain) and report how long it took.
 * `speed` is rendered seconds per wall-clock second; the audio thread needs comfortably more than 1 to keep up.
 */
export async function renderScene(
  doc: SceneDoc,
  opts: { seconds?: number; sampleRate?: number; earlyLimit?: number; playing?: boolean } = {},
): Promise<RenderedClick & { elapsedMs: number; speed: number; earlyPaths: number }> {
  const sampleRate = opts.sampleRate ?? 48000
  const seconds = opts.seconds ?? 5
  const ctx = new OfflineAudioContext(2, Math.ceil(seconds * sampleRate), sampleRate)
  const acoustics = computeAcoustics({
    enclosure: doc.enclosure,
    objects: doc.objects,
    listener: doc.listener.position,
    sources: doc.sources.map((s) => ({ id: s.id, position: s.position, size: s.size })),
  })
  if (opts.earlyLimit !== undefined) for (const s of acoustics.sources) s.early = s.early.slice(0, opts.earlyLimit)
  const engine = await AudioEngine.create(ctx, { immediate: true, room: acoustics.room })
  engine.syncSources(doc.sources.map((s) => ({ ...s, playing: opts.playing ?? true, mute: false, solo: false })))
  engine.setListener(doc.listener)
  engine.applyAcoustics(acoustics)
  const t0 = performance.now()
  const buf = await ctx.startRendering()
  const elapsedMs = performance.now() - t0
  return {
    left: buf.getChannelData(0).slice(),
    right: buf.getChannelData(1).slice(),
    sampleRate,
    elapsedMs,
    speed: seconds / (elapsedMs / 1000),
    earlyPaths: acoustics.sources.reduce((n, s) => n + s.early.length, 0),
  }
}
